import { ModalDialog } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { useRememberedSection } from './navigation-preferences';
import type { HostingSiteCatalogPage } from '@tosho/contracts';
import { usePaginationPageSize } from './pagination-preferences';
import { HostingBackupCatalogCard } from './HostingBackupCatalogCard';
import { HostingServerPleskStatus, HostingServerSitesButton } from './HostingServerPleskStatus';
import { HostingPleskDomainsPanel } from './HostingPleskDomainsPanel';
import { HostingPleskAccessDialog } from './HostingPleskAccessDialog';
import { HostingCockpitAccessDialog } from './HostingCockpitAccessDialog';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import type {
  HostingServerPage,
  HostingServerView,
  HostingControlPanelDeclaration,
  HostingProviderSummary,
} from '@tosho/contracts';
import { PrototypeIcon } from './PrototypeIcon';
import { HostingServerTechnology } from './HostingServerTechnology';
import { BusinessLogo } from './BusinessLogo';
import { HOSTING_SYSTEMS, declaredHostingPanels } from './hosting-technology-catalogue';
import { HostingControlPanelsFields } from './HostingControlPanelsFields';
import { HostingTechnologySelect } from './HostingTechnologySelect';
import { HostingProviderPicker } from './HostingProviderPicker';
import { HostingProvidersPanel } from './HostingProvidersPanel';
import { hostingProviderLogoUrl } from './hosting-provider-api';
import { clientDirectoryApi, type ClientRecord } from './client-directory-api';
import {
  hostingServerApi,
  hostingServerError,
  HostingServerRequestError,
  type ServerDescription,
} from './hosting-server-api';

/** Registre déclaratif Admin. La session/les droits sont vérifiés par l'API ;
 * ce composant ne fabrique ni connexion Plesk, ni observation ou Client.
 */
export function HostingServersPanel({
  csrfToken,
  organizationLabel,
  organizationLogoUrl = null,
  onReauthenticate,
  initialServer = null,
  initialSite = null,
  initialView,
}: {
  readonly csrfToken: string;
  readonly initialServer?: HostingServerView | null;
  readonly initialSite?: HostingSiteCatalogPage['items'][number] | null;
  readonly initialView?: 'sites' | 'hosting-providers' | undefined;
  readonly organizationLabel: string;
  readonly organizationLogoUrl?: string | null;
  readonly onReauthenticate?: (() => void) | undefined;
}): React.JSX.Element {
  const [page, setPage] = useState(0),
    [size, setSize] = usePaginationPageSize(),
    [visit, setVisit] = useState(0);
  const [data, setData] = useState<HostingServerPage | null>(null),
    [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null),
    [feedback, setFeedback] = useState<string | null>(null);
  const [editor, setEditor] = useState<HostingServerView | 'new' | null>(initialServer);
  const [hostingView, setHostingView] = useRememberedSection(
    'hosting-view',
    initialView ?? (initialSite !== null ? 'sites' : 'servers'),
    ['servers', 'sites', 'hosting-providers'],
    initialView !== undefined || initialSite !== null,
  );
  const showProviders = hostingView === 'hosting-providers';
  const showDomains = hostingView === 'sites';
  const [domainServer, setDomainServer] = useState<HostingServerView | null>(null);

  const domainsButton = useRef<HTMLButtonElement>(null);
  const completedVisit = useRef(-1);
  const pendingFocus = useRef<string | null>(null);
  useEffect(() => {
    if (!showDomains && !loading && completedVisit.current === visit && pendingFocus.current) {
      (
        document.getElementById(pendingFocus.current) ?? document.getElementById('hosting-refresh')
      )?.focus();
      pendingFocus.current = null;
    }
  }, [showDomains, loading, data, visit]);
  const [accessServer, setAccessServer] = useState<HostingServerView | null>(null);
  const [cockpitServer, setCockpitServer] = useState<HostingServerView | null>(null);
  const providersButton = useRef<HTMLButtonElement>(null),
    providersWereShown = useRef(false);
  useEffect(() => {
    if (providersWereShown.current && !showProviders) providersButton.current?.focus();
    providersWereShown.current = showProviders;
  }, [showProviders]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void hostingServerApi
      .list(page, size)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((reason) => {
        if (active) {
          setData(null);
          setError(hostingServerError(reason));
        }
      })
      .finally(() => {
        if (active) {
          completedVisit.current = visit;
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [page, size, visit]);
  const refresh = (): void => {
    setVisit((n) => n + 1);
  };
  if (showDomains)
    return (
      <HostingPleskDomainsPanel
        csrfToken={csrfToken}
        initialServer={domainServer}
        initialSite={initialSite}
        onReauthenticate={onReauthenticate}
        organizationLabel={organizationLabel}
        organizationLogoUrl={organizationLogoUrl}
        onClose={() => {
          pendingFocus.current = domainServer
            ? 'hosting-sites-' + domainServer.id
            : 'hosting-all-sites';
          setHostingView('servers');
          refresh();
        }}
      />
    );
  if (showProviders)
    return (
      <HostingProvidersPanel
        csrfToken={csrfToken}
        onClose={() => {
          setHostingView('servers');
          refresh();
        }}
      />
    );
  return (
    <div className="hosting-servers hosting-register">
      <header className="dashboard-heading hosting-servers__heading">
        <div>
          <p className="dashboard-heading__eyebrow">Réglages</p>
          <h1>Serveurs reliés</h1>
          <p>Le registre des serveurs de votre organisation et de vos Clients.</p>
        </div>
        <div className="hosting-servers__heading-actions">
          <button
            type="button"
            id="hosting-all-sites"
            ref={domainsButton}
            onClick={() => {
              setDomainServer(null);
              setHostingView('sites');
            }}
          >
            <PrototypeIcon name="network" /> Sites observés
          </button>
          <button
            type="button"
            ref={providersButton}
            onClick={() => setHostingView('hosting-providers')}
          >
            <PrototypeIcon name="database" /> Hébergeurs
          </button>
          <button
            type="button"
            className="hosting-servers__primary"
            onClick={() => {
              setFeedback(null);
              setEditor('new');
            }}
          >
            <PrototypeIcon name="plus" /> Ajouter un serveur
          </button>
        </div>
      </header>
      {feedback && (
        <p className="hosting-servers__feedback tosho-feedback is-success" role="status">
          {feedback}
        </p>
      )}
      <section className="hosting-servers__card" aria-label="Registre des serveurs">
        <div className="hosting-servers__toolbar hosting-servers__catalogue-toolbar">
          <div className="hosting-servers__toolbar-title">
            <PrototypeIcon name="database" />
            <div>
              <h2>Serveurs enregistrés</h2>
              <p>
                {data
                  ? `${data.total} serveur${data.total > 1 ? 's' : ''}`
                  : 'Chargement du registre'}{' '}
                <span className="hosting-register__catalogue-detail">
                  · Fiches et observations Plesk
                </span>
              </p>
            </div>
          </div>
          <button type="button" id="hosting-refresh" onClick={refresh} disabled={loading}>
            <PrototypeIcon name="refresh-cw" /> Actualiser
          </button>
        </div>
        {error ? (
          <p className="hosting-servers__error tosho-feedback is-error" role="alert">
            {error}
          </p>
        ) : loading ? (
          <p role="status" className="hosting-servers__empty">
            Chargement des serveurs…
          </p>
        ) : data?.items.length === 0 ? (
          <div className="hosting-servers__empty">
            <PrototypeIcon name="database" />
            <h3>Aucun serveur enregistré</h3>
            <p>
              Ajoutez un serveur administré par votre organisation ou un hébergement Client externe.
            </p>
          </div>
        ) : data ? (
          <table className="hosting-servers__table">
            <caption className="sr-only">Serveurs enregistrés et responsabilités déclarées</caption>
            <thead>
              <tr>
                <th>Serveur</th>
                <th>Propriétaire</th>
                <th>Administration</th>
                <th>Suivi du serveur</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((server) => (
                <tr key={server.id}>
                  <td data-label="Serveur">
                    <strong className="hosting-register__name">
                      <PrototypeIcon name="network" />
                      {server.label}
                    </strong>
                    <HostingServerTechnology
                      provider={
                        server.hostingProvider ? (
                          <BusinessLogo
                            name={server.hostingProvider.displayName}
                            logoUrl={hostingProviderLogoUrl(server.hostingProvider)}
                          />
                        ) : (
                          <span>{server.provider ?? 'Hébergeur non renseigné'}</span>
                        )
                      }
                      pleskInventory={server.pleskInventory}
                      operatingSystem={server.operatingSystem}
                      pleskDeclared={server.pleskDeclared}
                      {...(server.controlPanels === undefined
                        ? {}
                        : { controlPanels: server.controlPanels })}
                    />
                  </td>
                  <td data-label="Propriétaire" className="hosting-register__party">
                    <BusinessLogo
                      name={
                        server.ownerKind === 'ORGANIZATION'
                          ? organizationLabel
                          : (server.ownerClientDisplayName ?? 'Client lié')
                      }
                      logoUrl={
                        server.ownerKind === 'ORGANIZATION'
                          ? organizationLogoUrl
                          : server.ownerClientHasLogo && server.ownerClientId
                            ? `/api/clients/${encodeURIComponent(server.ownerClientId)}/logo?v=${server.ownerClientLogoVersion ?? 0}`
                            : null
                      }
                    />
                  </td>
                  <td data-label="Administration" className="hosting-register__party">
                    {server.administration === 'ORGANIZATION' ? (
                      <BusinessLogo name={organizationLabel} logoUrl={organizationLogoUrl} />
                    ) : (
                      <>
                        Externe<small>Descriptif uniquement</small>
                      </>
                    )}
                  </td>
                  <td data-label="Suivi du serveur">
                    <HostingServerPleskStatus server={server} />
                  </td>
                  <td className="hosting-servers__actions" data-label="Actions">
                    <div className="hosting-register__actions">
                      <HostingServerSitesButton
                        server={server}
                        onSites={() => {
                          setDomainServer(server);
                          setHostingView('sites');
                        }}
                      />
                      {server.administration === 'ORGANIZATION' &&
                      declaredHostingPanels(server).some(
                        (panel) => panel.kind === 'PLESK' && panel.present === true,
                      ) ? (
                        <button
                          type="button"
                          id={'hosting-settings-' + server.id}
                          aria-label={'Réglages Plesk — ' + server.label}
                          title={'Réglages Plesk — ' + server.label}
                          className="hosting-servers__icon-button"
                          onClick={() => setAccessServer(server)}
                        >
                          <PrototypeIcon name="settings" />
                        </button>
                      ) : null}
                      {server.administration === 'ORGANIZATION' &&
                      declaredHostingPanels(server).some(
                        (panel) => panel.kind === 'COCKPIT' && panel.present === true,
                      ) ? (
                        <button
                          type="button"
                          id={'hosting-cockpit-settings-' + server.id}
                          aria-label={'Réglages Cockpit — ' + server.label}
                          title={'Réglages Cockpit — ' + server.label}
                          className="hosting-servers__icon-button"
                          onClick={() => setCockpitServer(server)}
                        >
                          <PrototypeIcon name="settings" />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        aria-label={'Modifier ' + server.label}
                        title={'Modifier ' + server.label}
                        className="hosting-servers__icon-button"
                        onClick={() => {
                          setFeedback(null);
                          setEditor(server);
                        }}
                      >
                        <PrototypeIcon name="pencil" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <footer className="hosting-servers__pagination">
          <label>
            Par page{' '}
            <select
              value={size}
              onChange={(event) => {
                setSize(Number(event.target.value));
                setPage(0);
              }}
            >
              {[5, 10, 20, 30, 50, 75, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </label>
          <span>
            Page {page + 1}
            {data ? ' sur ' + Math.max(1, Math.ceil(data.total / size)) : ''}
          </span>
          <div>
            <button
              type="button"
              disabled={loading || page === 0}
              onClick={() => setPage((n) => n - 1)}
            >
              Précédente
            </button>
            <button
              type="button"
              disabled={loading || !data || (page + 1) * size >= data.total}
              onClick={() => setPage((n) => n + 1)}
            >
              Suivante
            </button>
          </div>
        </footer>
      </section>
      {accessServer ? (
        <HostingPleskAccessDialog
          server={accessServer}
          csrfToken={csrfToken}
          onClose={() => {
            pendingFocus.current = 'hosting-settings-' + accessServer.id;
            setAccessServer(null);
            refresh();
          }}
          onReauthenticate={onReauthenticate}
        />
      ) : null}
      {cockpitServer ? (
        <HostingCockpitAccessDialog
          server={cockpitServer}
          csrfToken={csrfToken}
          onClose={() => {
            pendingFocus.current = 'hosting-cockpit-settings-' + cockpitServer.id;
            setCockpitServer(null);
            refresh();
          }}
        />
      ) : null}
      {editor && (
        <ServerEditor
          server={editor === 'new' ? null : editor}
          organizationLabel={organizationLabel}
          csrfToken={csrfToken}
          onClose={() => {
            setEditor(null);
            refresh();
          }}
          onSaved={(server) => {
            setEditor(null);
            setFeedback(`Le serveur « ${server.label} » est enregistré.`);
            setPage(0);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function ServerEditor({
  server,
  organizationLabel,
  csrfToken,
  onClose,
  onSaved,
}: {
  readonly server: HostingServerView | null;
  readonly organizationLabel: string;
  readonly csrfToken: string;
  readonly onClose: () => void;
  readonly onSaved: (result: HostingServerView) => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null),
    title = useId();
  const [label, setLabel] = useState(server?.label ?? ''),
    [provider, setProvider] = useState(server?.provider ?? '');
  const [hostingProvider, setHostingProvider] = useState<HostingProviderSummary | null>(
    server?.hostingProvider ?? null,
  );
  const [os, setOs] = useState<ServerDescription['operatingSystem']>(
    server?.operatingSystem ?? null,
  );
  const [panels, setPanels] = useState<readonly HostingControlPanelDeclaration[]>(
    server
      ? (server.controlPanels ??
          (server.pleskDeclared === null ? [] : declaredHostingPanels(server)))
      : [],
  );
  const [owner, setOwner] = useState<'ORGANIZATION' | 'CLIENT'>(
    server?.ownerKind ?? 'ORGANIZATION',
  );
  const [administration, setAdministration] = useState<'ORGANIZATION' | 'EXTERNAL'>(
    server?.administration ?? 'ORGANIZATION',
  );
  const [client, setClient] = useState<ClientRecord | null>(null),
    [query, setQuery] = useState(''),
    [clientPage, setClientPage] = useState(0);
  const [clients, setClients] = useState<readonly ClientRecord[]>([]),
    [clientCount, setClientCount] = useState(0),
    [clientLoading, setClientLoading] = useState(false),
    [clientError, setClientError] = useState(false);
  const [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    [conflict, setConflict] = useState(false);
  useEffect(() => {
    const previous = document.activeElement;
    const element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  useEffect(() => {
    if (server || owner !== 'CLIENT') return;
    let active = true;
    setClientLoading(true);
    setClientError(false);
    const timer = window.setTimeout(() => {
      void clientDirectoryApi
        .search(query, clientPage, 'clients')
        .then((result) => {
          if (active) {
            setClients(
              result.clients.filter(
                (c) => c.stage === 'CLIENT' && c.origin !== 'FREEBE_LEGACY_UNREVIEWED',
              ),
            );
            setClientCount(result.clientCount);
          }
        })
        .catch(() => {
          if (active) {
            setClientError(true);
            setClients([]);
          }
        })
        .finally(() => {
          if (active) setClientLoading(false);
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [server, owner, query, clientPage]);
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (saving || conflict) return;
    if (!label.trim() || (!server && owner === 'CLIENT' && !client)) {
      setError('Indiquez le nom du serveur et choisissez son propriétaire.');
      return;
    }
    setSaving(true);
    setError(null);
    const description = {
      label: label.trim(),
      provider: provider.trim() || null,
      hostingProviderId: hostingProvider?.id ?? null,
      operatingSystem: os,
      pleskDeclared: panels.find((panel) => panel.kind === 'PLESK')?.present ?? null,
      controlPanels: panels,
    };
    try {
      const result = server
        ? await hostingServerApi.describe(server, description, csrfToken)
        : await hostingServerApi.create(
            {
              ...description,
              ownerKind: owner,
              ownerClientId: owner === 'CLIENT' ? client!.id : null,
              administration: owner === 'ORGANIZATION' ? 'ORGANIZATION' : administration,
            },
            csrfToken,
          );
      onSaved(result);
    } catch (reason) {
      setError(hostingServerError(reason));
      setConflict(
        reason instanceof HostingServerRequestError && reason.code === 'HOSTING_SERVER_CONFLICT',
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <ModalDialog
      ref={dialog}
      className="hosting-servers-editor"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        if (!saving) onClose();
      }}
      close={
        <ModalCloseButton
          className="tosho-modal-close"
          aria-label="Fermer"
          onClick={onClose}
          disabled={saving}
        />
      }
    >
      <form
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <header className="tosho-modal-header">
          <div>
            <p className="hosting-servers-editor__eyebrow">Registre des serveurs</p>
            <h2 id={title}>{server ? 'Modifier le serveur' : 'Ajouter un serveur'}</h2>
          </div>
        </header>
        <p className="hosting-servers__help">
          Cette fiche est déclarative. Elle ne crée aucune connexion au serveur.
        </p>
        {error && (
          <p className="hosting-servers__error tosho-feedback is-error" role="alert">
            {error}
          </p>
        )}
        <fieldset disabled={saving || conflict}>
          <label>
            Nom du serveur
            <input
              autoFocus
              required
              maxLength={160}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>
          <HostingProviderPicker
            value={hostingProvider}
            onChange={(selected) => {
              setHostingProvider(selected);
              if (selected) setProvider(selected.displayName);
            }}
          />
          {!hostingProvider && (
            <label>
              <span>Hébergeur (facultatif)</span>
              <input
                maxLength={160}
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              />
            </label>
          )}
          {server ? (
            <div className="hosting-servers-editor__ownership">
              <p>
                <strong>Propriétaire :</strong>{' '}
                {owner === 'ORGANIZATION'
                  ? organizationLabel
                  : (server.ownerClientDisplayName ?? 'Client lié')}
              </p>
              <p>
                <strong>Administration :</strong>{' '}
                {administration === 'ORGANIZATION' ? organizationLabel : 'Externe'}
              </p>
              <small>La propriété et l’administration sont conservées lors d’une correction.</small>
            </div>
          ) : (
            <>
              <label>
                Propriétaire
                <select
                  value={owner}
                  onChange={(e) => {
                    setOwner(e.target.value as 'ORGANIZATION' | 'CLIENT');
                    setClient(null);
                  }}
                >
                  <option value="ORGANIZATION">{organizationLabel}</option>
                  <option value="CLIENT">Un Client</option>
                </select>
              </label>
              {owner === 'CLIENT' && (
                <div className="hosting-servers-editor__client">
                  <label>
                    Rechercher un Client
                    <input
                      value={query}
                      onChange={(e) => {
                        setQuery(e.target.value);
                        setClientPage(0);
                        setClient(null);
                      }}
                      maxLength={120}
                    />
                  </label>
                  {client && (
                    <p role="status">
                      Client choisi : <strong>{client.displayName}</strong>
                    </p>
                  )}
                  {clientLoading ? (
                    <p role="status">Recherche des Clients…</p>
                  ) : clientError ? (
                    <p className="tosho-feedback is-error" role="alert">
                      Impossible de charger les Clients. Modifiez la recherche pour réessayer.
                    </p>
                  ) : (
                    <>
                      <ul aria-label="Clients disponibles">
                        {clients.map((item) => (
                          <li key={item.id}>
                            <button
                              type="button"
                              aria-pressed={client?.id === item.id}
                              onClick={() => setClient(item)}
                            >
                              {item.displayName}
                            </button>
                          </li>
                        ))}
                      </ul>
                      {clients.length === 0 && <p>Aucun Client confirmé dans ces résultats.</p>}
                      <div className="hosting-servers-editor__client-pages">
                        <button
                          type="button"
                          disabled={clientPage === 0}
                          onClick={() => setClientPage((n) => n - 1)}
                        >
                          Clients précédents
                        </button>
                        <button
                          type="button"
                          disabled={(clientPage + 1) * 50 >= clientCount}
                          onClick={() => setClientPage((n) => n + 1)}
                        >
                          Clients suivants
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              {owner === 'CLIENT' && (
                <label>
                  Administration
                  <select
                    value={administration}
                    onChange={(e) =>
                      setAdministration(e.target.value as 'ORGANIZATION' | 'EXTERNAL')
                    }
                  >
                    <option value="ORGANIZATION">{organizationLabel}</option>
                    <option value="EXTERNAL">Externe — descriptif uniquement</option>
                  </select>
                </label>
              )}
            </>
          )}
          <div className="hosting-servers-editor__technical">
            <HostingTechnologySelect
              label="OS serveur"
              options={Object.entries(HOSTING_SYSTEMS).map(([value, system]) => ({
                value,
                name: system.name,
                logo: system.logo,
              }))}
              values={os === null ? [] : [os]}
              onChange={(selected) =>
                setOs((selected[0] ?? null) as ServerDescription['operatingSystem'])
              }
            />
            <HostingControlPanelsFields values={panels} onChange={setPanels} />
          </div>
        </fieldset>
        {server && <HostingBackupCatalogCard serverId={server.id} />}
        <footer>
          <button type="button" onClick={onClose} disabled={saving}>
            {conflict ? 'Fermer et actualiser' : 'Annuler'}
          </button>
          <button
            type="submit"
            className="hosting-servers__primary"
            disabled={
              saving || conflict || !label.trim() || (!server && owner === 'CLIENT' && !client)
            }
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
